import { useCallback, useMemo, useState } from "react";

import clsx from "clsx";
import { v4 as uuidv4 } from "uuid";

import { TileModal } from "@features/metrics-explorer/components";
import { IndexCheckbox } from "@features/metrics-explorer/components/metric-card/index-checkbox";
import { NBX_SUPPORTED_METRICS } from "@features/metrics-explorer/constants";
import { useMetricsExplorerContext } from "@features/metrics-explorer/hooks";
import { type TileDescriptor } from "@features/metrics-explorer/types";
import { useFeatureFlags } from "@hooks/http";

import { useSalesV4Context } from "../hooks";
import { isStackedChartAvailable } from "../utils";

interface Props {
  tile: TileDescriptor;
}

export const MiniMetricCard = ({ tile }: Props) => {
  const { hasFeatureFlag } = useFeatureFlags();
  const { chartState, setChartState } = useSalesV4Context();
  const { filters, buildMetrics } = useMetricsExplorerContext();

  const [isEditing, setIsEditing] = useState(false);

  const metrics = useMemo(() => {
    if (!tile) {
      return [];
    }

    return buildMetrics({
      attributionModel: tile.attributionModel ?? filters.attributionModel,
      attributionWindow: tile.attributionWindow ?? filters.attributionWindow,
      accountingMode: tile.accountingMode ?? filters.accountingMode,
    });
  }, [
    buildMetrics,
    filters.attributionModel,
    filters.attributionWindow,
    filters.accountingMode,
    tile,
    tile?.attributionModel,
    tile?.attributionWindow,
    tile?.accountingMode,
  ]);

  const metric = useMemo(() => {
    return tile && metrics.find((e) => e.id === tile.metricId);
  }, [tile, metrics]);

  const deleteTile = useCallback(() => {
    if (!tile) {
      return;
    }

    let newTiles = chartState.metrics
      .filter((e) => e.id !== tile.id)
      .map((e, idx) => ({ ...e, index: idx }));
    const activeIndex = newTiles.findIndex((e) => e.active);

    if (activeIndex === -1) {
      newTiles = newTiles.map((e, idx) => ({
        ...e,
        active: idx === 0,
      }));
    }

    setChartState({
      ...chartState,
      metrics: newTiles,
      displayMode:
        chartState.displayMode === "stacked" &&
        !isStackedChartAvailable(newTiles)
          ? "split"
          : chartState.displayMode,
    });
  }, [chartState, tile, setChartState]);

  if (!tile || !metric) {
    return null;
  }

  return (
    <>
      <div
        className={clsx(
          "h-8 border border-v2-tertiary rounded-md cursor-pointer",
          "font-semibold flex items-center justify-center gap-1.5 px-2",
          "hover:border-v2-highlight-alert-hover",
        )}
        onClick={() => {
          setIsEditing(true);
        }}
      >
        <div>{tile.name ?? metric.name}</div>
        {hasFeatureFlag("enableNorthbeamIndex") &&
          NBX_SUPPORTED_METRICS.includes(tile.metricId) && (
            <IndexCheckbox
              className="h-[22px]"
              title="NBX"
              value={!!tile.showNBX}
              onChange={(val) => {
                setChartState({
                  ...chartState,
                  metrics: chartState.metrics.map((e) =>
                    e.id === tile.id ? { ...e, showNBX: val } : e,
                  ),
                });
              }}
            />
          )}
        <button
          className={clsx(
            "w-4 h-4 flex items-center justify-center rounded bg-v2-secondary text-v2-secondary",
            "hover:text-v2-action-negative hover:bg-v2-highlight-alert",
          )}
          onClick={(e) => {
            e.stopPropagation();
            deleteTile();
          }}
        >
          <i className={clsx("fa-regular fa-xmark text-xs")}></i>
        </button>
      </div>
      {isEditing && (
        <TileModal
          isOpen={isEditing}
          initialName={tile.name || undefined}
          initialMetricId={tile.metricId}
          initialAttributionModel={tile.attributionModel ?? undefined}
          initialAttributionWindow={tile.attributionWindow ?? undefined}
          initialAccountingMode={tile.accountingMode ?? undefined}
          initialCompareDateRange={tile.compareDateRange ?? undefined}
          initialFilterQuery={tile.filterQuery ?? undefined}
          initialFilters={tile.filters}
          initialAdvancedSearch={tile.advancedSearch ?? undefined}
          onClose={() => {
            setIsEditing(false);
          }}
          onSubmit={(value) => {
            const newTiles = chartState.metrics.map((e) =>
              e.id === tile.id
                ? {
                    ...e,
                    name: value.name,
                    metricId: value.metricId,
                    attributionModel: value.attributionModel,
                    accountingMode: value.accountingMode,
                    attributionWindow: value.attributionWindow,
                    compareDateRange: value.compareDateRange,
                    filterQuery: value.filterQuery,
                    filters: value.filters,
                    advancedSearch: value.advancedSearch,
                  }
                : e,
            );

            setChartState({ ...chartState, metrics: newTiles });
            setIsEditing(false);
          }}
          onDuplicate={() => {
            const newTiles = [
              ...chartState.metrics,
              {
                ...tile,
                active: false,
                id: uuidv4(),
                index: chartState.metrics.length,
              },
            ];

            setChartState({ ...chartState, metrics: newTiles });

            setIsEditing(false);
          }}
          onDelete={() => {
            deleteTile();
            setIsEditing(false);
          }}
        />
      )}
    </>
  );
};
